import { SectionGroups } from "@/components/marketing/SectionLayout";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { breadcrumbJsonLd } from "@/content/schema";
import { SITE, abs } from "@/content/siteConfig";

const TITLE = "Research: Original Paid Media Data";
const DESCRIPTION =
  "Original research: a browser-level scan of conversion tracking across more than 9,000 home-services websites. Method included.";
const URL = abs("/research");

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "How was the conversion tracking data collected?",
    a: "Every site was loaded in a real headless browser and allowed to execute JavaScript, then its rendered DOM was inspected for tracking signals. That matters, because a raw HTML fetch misses anything a tag manager injects at runtime and would overstate how many sites are untracked. Detection is deterministic pattern-matching rather than a model, so the same site produces the same verdict every time it is scanned.",
  },
  {
    q: "What counts as a business with no conversion tracking?",
    a: "A site is counted as untracked only when a rendered browser session finds no Google Tag Manager container, no GA4, no Google Ads conversion tag, no Meta pixel, no Microsoft UET and no call-tracking script. One signal is enough to move a site out of that group. The measure is about the presence of a mechanism, not about whether that mechanism is configured correctly.",
  },
  {
    q: "Why is the 27.4% figure described as a ceiling?",
    a: "Three things push it upward. Server-side tagging is largely invisible to a page scan, so a fully tracked business can read as untracked. Consent-gated tags may never fire for an automated visitor that does not click accept. And a business can record leads by hand without any script at all. The true rate is therefore at or below the published one, not above it.",
  },
];

export const Route = createFileRoute("/research/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: URL },
      { property: "og:image", content: abs(SITE.ogImagePath) },
      { name: "twitter:image", content: abs(SITE.ogImagePath) },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Research", path: "/research" },
        ]),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          name: "Conversion tracking",
          description:
            "Conversion tracking is the mechanism that records what happened after someone clicked an ad (a form submitted, a call placed, a purchase made) and reports it back to the ad platform.",
          url: abs("/glossary/conversion-tracking"),
          inDefinedTermSet: abs("/glossary"),
        }),
      },
    ],
  }),
  component: ResearchIndex,
});

function ResearchIndex() {
  return (
    <SiteShell>
      <div className="wrap" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="reading-layout" style={{ maxWidth: 720, margin: "0 auto" }}>
          <h1 style={{ fontSize: "clamp(32px, 5vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em" }}>
            Research
          </h1>
          <p className="prose-lead mt-6" style={{ fontSize: 19, lineHeight: 1.55, color: "rgba(0,0,0,0.72)" }}>
            First-party data we collect ourselves, published in full with the method attached. Each
            report is updated in place as its dataset grows, so the URL you cite stays current.
          </p>

          <SectionGroups className="blog-content section-groups mt-12">
            <h2>What we measured</h2>
            <p>
              Most published work on ad measurement is survey work: marketers are asked what they
              believe is installed on their websites, and the answers are totalled up. That produces
              a picture of what an industry thinks about itself. We wanted the other thing: what is
              actually running on the page when a customer arrives on it.
            </p>
            <p>
              So the question we set out to answer was narrow and checkable. For a home-services
              business that is spending money on ads right now, is there any mechanism on its website
              capable of recording that a lead happened? Not whether the mechanism is configured
              well, not whether the numbers it produces are trusted internally, simply whether one
              exists at all. That is the floor beneath every other measurement question, and it turns
              out to be missing far more often than the industry assumes.
            </p>

            <h2>How we measured it</h2>
            <p>
              We have loaded more than 9,000 home-services business websites
              in a real headless browser, let each page execute its JavaScript, and inspected the
              rendered DOM for tracking signals. The rendered part is the whole point. A raw HTML
              fetch cannot see anything a tag manager injects at runtime, so a fetch-based scan
              would report a far higher share of untracked sites than really exists. Detection is
              deterministic pattern-matching rather than a model, which means the same site returns
              the same verdict every time it is scanned and the dataset can be regrown without the
              earlier rows drifting.
            </p>
            <p>
              A site is counted as untracked only when the browser session finds none of the
              following: a Google Tag Manager container, GA4, a Google Ads conversion tag, a Meta
              pixel, Microsoft UET, or a call-tracking script. A single one of those is enough to
              move a site out of the untracked group. Separately, advertising status was checked
              against Google&apos;s Ads Transparency Center, so &quot;confirmed advertiser&quot;
              means live ads were visible there at the time of the scan rather than inferred from the
              site itself.
            </p>

            <h2>What we found</h2>
            <p>
              Of the businesses confirmed to be running ads, more than 1 in 4 had no conversion
              tracking of any kind. Not misconfigured, not
              partially firing: nothing on the page capable of recording a lead. Across the full
              sample, advertising or not, more than two thirds of sites had nothing installed.
            </p>
            <p>
              The individual platform numbers say the same thing from another direction. GA4 was
              present on 26% of all sites, a Google Ads conversion tag on 21%, and Google Tag Manager
              on 18%. Those are the tools the entire optimization apparatus of paid search assumes
              are in place.
            </p>
            <p>
              These figures are from the September 6, 2026 edition of the report. The scan runs
              continuously, so the current totals are higher, see{" "}
              <Link to="/research/conversion-tracking-report" style={{ color: "#002FA7", textDecoration: "underline" }}>
                the full report
              </Link>{" "}
              for the latest counts.
            </p>
            <p>
              The rate is also not evenly spread. Broken out by trade, the top of the table sits near
              a coin flip and the bottom sits in the low teens, which is a wider spread than most
              operators would guess about their own vertical. The report publishes every trade with
              at least 15 confirmed advertisers, alongside the advertiser count for each row, so a
              reader can see which rates rest on a thin sample before quoting one.
            </p>

            <h2>What it means for an advertiser</h2>
            <p>
              An advertiser with no conversion tracking is not measuring badly. They are not
              measuring at all. Every decision in that account (which keyword to raise, which
              campaign to cut, whether the channel is worth running) is being made on clicks. Clicks
              and jobs are not the same thing, and the gap between them is where budgets disappear. A
              campaign producing expensive clicks and cheap jobs reads as a failure. A campaign
              producing cheap clicks and no jobs reads as a success, and can run that way for years
              before anyone catches it.
            </p>
            <p>
              The practical implication is not that a business should install more tags. It is that
              nothing downstream (bid strategy, budget allocation, creative testing, agency
              reporting) carries any weight until this layer exists and is verified to fire. The
              cheapest available improvement in most accounts we inspect is not a new campaign; it is
              making the existing measurement true.
            </p>

            <h2>Limitations of the method</h2>
            <p>
              The 27.4% is a ceiling rather than an exact figure, and it is almost certainly slightly
              too high. Server-side tagging is largely invisible to a page-level scan: a business
              running a server-side container can be fully tracked while a scan sees nothing.
              Consent-gated tags may never fire for an automated visitor that does not click accept.
              And a business can record leads perfectly well with a phone log and a notepad; that is
              a measurement system, just not one a campaign can be optimized against.
            </p>
            <p>
              The sample is also not a national random sample. It is a large, systematically
              collected set of home-services businesses drawn from metropolitan areas in Arizona,
              California and Nevada, and it should be read as that. Absence of ads in the Ads Transparency
              Center is not proof a business is not advertising, so businesses we could not confirm
              are excluded from the advertiser group entirely rather than counted as
              non-advertisers, which keeps the denominator honest at the cost of making it smaller.
            </p>
            <p>
              All of the above is published on the report itself, with the full trade table and the
              exact detection criteria. The page is updated in place as the dataset grows, so cite
              the URL rather than a figure in isolation.
            </p>

            <h2>Questions about the method</h2>
            {FAQS.map((f) => (
              <div key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </SectionGroups>

          <div className="mt-14">
            <Link
              to="/research/conversion-tracking-report"
              className="block rounded-lg border p-6 transition-colors hover:border-[rgba(0,47,167,0.4)]"
              style={{ borderColor: "rgba(0,0,0,0.12)", background: "#fbfaf6" }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "rgba(0,47,167,0.9)",
                }}
              >
                Original research
              </div>
              <h2 className="mt-3" style={{ fontSize: 26, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
                The Conversion Tracking Report
              </h2>
              <p className="mt-3" style={{ fontSize: 16.5, lineHeight: 1.6, color: "rgba(0,0,0,0.72)" }}>
                We loaded more than 9,000 home-services business websites in a real browser. More than 1 in 4
                of the ones confirmed to be running ads had no conversion tracking at all. Full data by trade,
                with the method and its limits spelled out.
              </p>
            </Link>
          </div>

          <p className="mt-8" style={{ fontSize: 16, lineHeight: 1.6, color: "rgba(0,0,0,0.62)" }}>
            To see which side of the 27.4% your own site sits on, run the{" "}
            <Link to="/audit" style={{ color: "#002FA7", textDecoration: "underline" }}>
              free conversion tracking checker
            </Link>{" "}
            or read the{" "}
            <Link to="/glossary/$slug" params={{ slug: "conversion-tracking" }} style={{ color: "#002FA7", textDecoration: "underline" }}>
              conversion tracking definition
            </Link>
            .
          </p>
        </div>
      </div>
    </SiteShell>
  );
}
